/**
 * Typed API layer for the Ember v1.5 product — mirrors docs/ember-v1-api.md
 * (which mirrors 新设计文档 §12). Thin wrappers over the shared apiFetch so
 * auth cookies, trace headers and 401 cache-clearing behave exactly like the
 * rest of the SPA. Every POST is one user action → one Idempotency-Key.
 */

import { apiFetch, apiFetchStream, ApiError } from '../auth/api-client';
import { browserTimezone } from '../../../../shared-web/timezone';
import type { MascotFace } from '../../../../shared/ember-mascot.js';

// ---------------------------------------------------------------------------
// Shapes (§12)
// ---------------------------------------------------------------------------

/**
 * The notebook's lock as the server reports it (§4.12). The password's digest
 * never ships — the browser learns only that there is one, and asks the server
 * to open it. `recovery` says whether the `Forgot it?` door can actually text
 * this account: `reason` is 'no_handle' (no phone connected to the account) or
 * 'opted_out' (they said STOP), and '' when a link can go out.
 */
export interface NotebookLockResponse {
  ok: true;
  locked: boolean;
  set_at: string | null;
  recovery: { available: boolean; hint: string; reason: string };
  /**
   * Confirm it's you (2026-10-19 Riley): the account's own sign-in methods that can stand in for a forgotten
   * password (google | apple | phone), and the masked number a phone code goes to. Absent from older edges.
   */
  reauth?: { methods: string[]; phone_hint: string };
}

export type MatchingStatus = 'pending' | 'complete' | 'failed';

export interface Topic {
  id: string;
  slug: string;
  title: string;
  short_prompt: string;
}

export interface HowStep { who: string; text: string; gate?: string; shot: string }
export interface HowItWorks {
  sub: string;
  always: { label: string; steps: HowStep[] };
  sometimes: { label: string; sub: string; steps: HowStep[] };
  foot: string;
}

export interface HomeResponse {
  /** The homepage track this payload was read for (`/?t=`), absent on the plain homepage. */
  track?: string;
  hero: {
    headline: string;
    /** Under the headline (2026-09-15): the one line — AI, provenance, contract. */
    supporting_text: string;
    /** The composer's placeholder. */
    composer_placeholder: string;
    /** The placeholder once this browser has a conversation going (2026-09-16): both readings of the field, said once. */
    composer_placeholder_return: string;
    /** Under the composer: the permission model in one line. */
    trust_line: string;
    /** The second screen (2026-09-13): four cards, each a picture (`shot`, WhyShots), a small label and one paragraph. */
    why_ember: { eyebrow: string; text: string; differences: { label: string; text: string; shot: string }[] };
    /**
     * How it works as one flow (2026-09-12): the steps every conversation
     * comes to, a fork, then the steps that sometimes follow. Each step has
     * a drawn picture (`shot`, ConnectionShots).
     */
    how_it_works: HowItWorks;
  };
  /** §4.17 the Banyan's public lights; absent when the tree is not wired. */
  tree?: TreeResponse;
  /**
   * The console-editable FAQ block under How it works (2026-09-10). It rides
   * this payload — rather than a fetch of its own — because the answers have
   * to be in the server-rendered HTML: that is what the FAQPage JSON-LD on `/`
   * describes. Absent when the list is empty.
   */
  faq?: { question: string; answer: string }[];
}

/**
 * A life node (v4.0 §4.17): a recurring human situation at search-intent
 * granularity — no owner, nobody's words. `x`/`y` is its fixed place in the
 * canopy (normalized), present on public nodes only.
 */
export interface TreeNode {
  id: string;
  slug: string;
  title: string;
  definition: string;
  /**
   * The first line a person standing here would type into the landing
   * composer, in their own voice (§6.3.6, 2026-09-13). null until the pass
   * has written it; the composer lists only lights that have one.
   */
  first_line: string | null;
  status: 'latent' | 'public' | 'retired';
  /** Distinct people whose Embers stood here. */
  people_count: number;
  /** Position inside the current window (normalized canopy coordinates). */
  x?: number;
  y?: number;
  /** The window's centre. */
  focus?: boolean;
  /** One of the viewer's own lights. */
  mine?: boolean;
  /** One of the lights the homepage track named (tracks.ts); the composer offers these first. */
  track?: boolean;
}

export interface TreeResponse { nodes: TreeNode[] }
/** One window of the tree (§4.17): a centre and its neighbourhood, or today's tree. */
export interface TreeViewResponse extends TreeResponse {
  mode: 'focus' | 'mine' | 'today' | 'track';
  focus_id: string | null;
  /** The track this window was drawn for, when the read named one. */
  track?: string;
}
export interface TreeYouResponse {
  animal: string;
  /** A visitor's own latest kept Ember (2026-09-06); null signed in or before any telling. */
  your_ember: { conversation_id: string; title: string; /** how many kept Embers this browser holds */ count: number; /** a voice telling (its page is /result); a text telling's Ember lives in the thread */ finalized?: boolean } | null;
  /**
   * The tail of this browser's or account's open conversation (2026-09-16):
   * Ember's last line and when — only once the person has spoken in it. Null
   * for a browser that has never talked, or that only opened the chat and left.
   */
  open_thread: OpenThread | null;
  /**
   * The bar over the composer (2026-10-20 founder; edge chats.homeTail): the conversations with something unread,
   * in turn — or Ember's last line in the thread when nothing is — rows of the Chats list's shape. Empty: no bar.
   * `clock` is the person's (null for a guest: the device's). The web reads this; `open_thread` is the released apps'.
   */
  tail: HomeTail;
  /** The account has the app (2026-10-26): pushes registered, or an app used in the last 90 days. Always false for a visitor. */
  has_app?: boolean;
}
export interface OpenThread { ember_line: string; at: string }
export interface HomeTail { clock: ChatsResponse['clock'] | null; rows: Array<ChatEmberRow | ChatRow> }
/**
 * Ask this light (§4.17, 2026-09-21): whether the light takes questions (enough
 * people with accounts stood here), the counts under an answer, and the
 * offered questions, the most-asked first (answers are asked for, never
 * carried here).
 */
export interface PlaceAsk {
  eligible: boolean;
  counts: { people: number; embers: number; came_back: number };
  /** `answer` is the question's last answer (2026-10-11), read on the page in one closed section. */
  questions: Array<{ id: string; text: string; answer?: { account: string; view: string; suggestion: string } }>;
}
/** One entry of the light's week-by-week log (§4.17, 2026-09-21); `week_start` is the PT Monday, YYYY-MM-DD. */
export interface PlaceWeek { week_start: string; new_embers: number; new_people: number; text: string; written_at: string }
/**
 * What one question put to a light came back with (2026-09-22: three parts).
 * `account` is what the people here went through, empty when the light held
 * nothing on it; `view` is Ember's view, `suggestion` the one thing under it —
 * each empty when the people here gave none.
 */
export interface LightAnswer {
  question: { id: string | null; text: string };
  answer: { account: string; view: string; suggestion: string };
  answered: boolean;
  counts: { people: number; embers: number; came_back: number };
}
/**
 * The SEO layer under a Place page (2026-09-17): the words a search reader
 * needs, written per place and published by an editor. Absent for the places
 * that have none yet; `published:false` only on the token-gated admin preview.
 */
export interface PlaceSeo {
  title: string;
  description: string;
  situation: { heading: string; intro: string; cards: Array<{ title: string; body: string }> };
  published: boolean;
}
export interface PlaceResponse { node: TreeNode; neighbours: TreeNode[]; ask?: PlaceAsk | null; weeks?: PlaceWeek[]; seo?: PlaceSeo | null }

/** Resolved page media for a full Ember; empty string = not there (yet). */
export interface EmberMedia {
  header_image_url: string;
  /** Optional responsive candidates for compact public feed thumbnails. */
  header_image_srcset?: string;
  music_url: string;
  /** A header image is still being generated — hold its frame with the placeholder. */
  header_image_pending?: boolean;
  /** A music brief exists and its audio is still resolving. */
  music_pending?: boolean;
}

/**
 * Human Presence (§0.9): the anonymous person behind a readable Ember —
 * curated avatar key, the owner-approved line (may be absent), and the
 * person-page token. Server-omitted (null) on veiled payloads, when the owner
 * switched the Ember's presence off, and on seeded/synthetic content.
 */
export interface Teller {
  avatar_id: string;
  line: string | null;
  presence_token: string;
}

/**
 * §0.9 provenance receipt (2026-08-31 拍板): the facts behind the Human-told
 * badge — the exact day the telling happened and its channel. Server-omitted
 * on veiled payloads and on seeded/synthetic content; independent of
 * show_teller (an unsigned Ember is still human-told).
 */
/**
 * The three doors a telling can arrive through (§0.9 receipt, 2026-09-04):
 * typed on the chat page, texted from the person's own phone, or spoken on a
 * call. The mic in the chat composer is dictation, not a fourth door — those
 * words land as a typed turn.
 */
export type TellingChannel = 'web_chat' | 'sms' | 'voice';

export interface EmberProvenance {
  told_at: string | null;
  channel: TellingChannel;
}

/**
 * S17 (§4.18): what the person holding a hand-off link reads — the whole
 * Ember, its cover, the receipt. No presence, no status, nothing else.
 */
export interface HandoffResponse {
  ember: {
    id: string;
    title: string;
    canonical_text: string;
    language: string;
    media?: EmberMedia;
    photos?: EmberPhoto[];
  };
  provenance: EmberProvenance | null;
}

/** Public data the edge can place in the initial document before JavaScript. */
export type EmberSsrData =
  | {
      page: 'home';
      path: '/';
      home: HomeResponse;
    }
  | { page: 'handoff'; path: string; detail: HandoffResponse }
  | { page: 'letter_handoff'; path: string; detail: LetterHandoffResponse }
  | { page: 'place'; path: string; place: PlaceResponse };

/** One photo handed over from the composer's + button, as the browser shows it. */
export interface MessageAttachment {
  media_id: string;
  url: string;
  width: number | null;
  height: number | null;
}

export interface ConversationMessage {
  /**
   * user / assistant in a conversation; in a room's thread (§4.21, 2026-09-25
   * 二次拍板, the same shell) also peer — another member, `speaker` says who —
   * and system, the room's own line (joined / left / quieted / taken down).
   */
  role: 'user' | 'assistant' | 'peer' | 'system';
  content: string;
  turn_index: number;
  /** A peer's name and avatar as they exist in this room alone; `seat` their place among the others (the name's colour). */
  speaker?: { name: string; avatar: string | null; seat?: number };
  /** Absent on tellings that never used the + button. */
  attachments?: MessageAttachment[];
  /**
   * Which door this turn arrived through — SMS and web chat are one logical
   * channel writing the same conversation. Null on turns written before the
   * marker existed (migration 160); absent on locally-composed optimistic turns.
   */
  source?: 'web_chat' | 'sms' | 'voice' | 'letter' | 'notice' | null;
  /** When the turn was written (server rows only; absent on optimistic turns). */
  created_at?: string;
  /**
   * Ember's face beside this turn (2026-09-13): one of the mascot's faces,
   * chosen for the turn. Absent (a turn from before, or the person's own) the
   * thread draws `listening`.
   */
  face?: MascotFace;
  /** On a weekly-letter turn (source 'letter', §4.20): the letter the thread's cover opens. */
  letter_id?: string;
  /** On a weekly-letter turn whose letter was drawn with its path (2026-11-04): the cover's picture. */
  letter_cover?: string;
  /**
   * On every notice turn (source 'notice', §4.14): the line the thread draws (2026-10-19 拍板) — `head`, what
   * happened, and `body`, the specifics, the text's own first two paragraphs — and, when its fact still has a
   * page (2026-09-30), the kind of fact and where it lives: the line is the way in. `folded` (2026-10-21): a
   * newer notice about the same thing stands right after it, with nothing said between; the thread does not draw it.
   */
  notice?: { head: string; body: string; kind?: string; destination?: string; folded?: boolean };
  /**
   * On a texted turn of Ember's that carried a link (source 'sms'; 2026-11-04): the door the thread draws under the
   * bubble in place of the raw URL — `destination`, the page the link leads to (absent when the link's row is
   * gone), `url`, the link itself, and `label`, the door's words. The turn's content comes without the URL.
   */
  way_in?: { url: string; destination?: string; label: string };
}

/** Accepted photo, waiting for the turn that will carry it. */
export interface UploadedMedia extends MessageAttachment, PhotoRoom {
  limit: number;
}

/**
 * The stretch's photos (2026-10-19 Riley): how many it holds — sent since the last write-up, plus the ones
 * waiting in the tray — and the cap. On the conversation read, every upload, the over_cap refusal, and
 * GET /photos (which adds photos_left). The composer counts with these, never with the turns it has loaded.
 */
export interface PhotoRoom {
  photos_used: number;
  photos_limit: number;
  photos_left?: number;
  photos_per_message?: number;
}

/** Photos one message carries at most by default (2026-10-19 Riley; edge EMBER_PHOTOS_PER_MESSAGE, also sent as photos_per_message). The value in force is clientConfig().photos_per_message (client-config.ts). */
export const PHOTOS_PER_MESSAGE = 6;

/** The room a PhotoRoom body describes, or null when the body is not one (an edge older than 2026-10-19). */
export function photoRoomOf(body: unknown): { used: number; limit: number } | null {
  if (!body || typeof body !== 'object') return null;
  const { photos_used: used, photos_limit: limit } = body as Partial<PhotoRoom>;
  return typeof used === 'number' && typeof limit === 'number' ? { used, limit } : null;
}

/** The room on an over_cap refusal's body (2026-10-19 Riley). */
export function photoRoomOfError(err: unknown): { used: number; limit: number } | null {
  return err instanceof ApiError ? photoRoomOf(err.body) : null;
}

/** Photos per telling by default — the stretch not yet written into an Ember. Mirrors EMBER_CHAT_MAX_PHOTOS on the edge; the value in force is clientConfig().photos_per_conversation (2026-10-19 Riley). */
export const MAX_CHAT_PHOTOS = 100;

export type ConversationStatus = 'active' | 'finalizing' | 'finalized' | 'needs_more_context' | 'failed';

/**
 * An Ember written from this conversation (2026-09-12): the idle sweep wrote
 * it after the person went quiet, from what was said up to `after_turn_index`
 * — the thread draws it there, in place.
 */
/**
 * One key sentence of an Ember (§6.3, 2026-09-15): a sentence the writer named
 * as carrying the piece, verbatim from the text, and the speaker's own words
 * it rests on. The page washes the sentence in the prose; a tap opens the words.
 */
export interface KeySentence {
  sentence: string;
  rests_on: string;
}

/**
 * One of the speaker's own photos placed in an Ember (2026-09-15): it sits
 * under the paragraph of the canonical text that ends at `after_char` (a
 * UTF-16 offset, the source_map ruler). Absent or [] on Embers without any.
 */
export interface EmberPhoto {
  after_char: number;
  url: string;
  width: number | null;
  height: number | null;
}

/**
 * The Ember as the person who told it reads it (§6.3) — what EmberBody draws,
 * and the one shape edge's `emberRead` puts on every owner face (the thread's
 * `embers[]`, `/api/me/embers/:id`, the Result page). Each face adds what
 * stands around it.
 */
export interface EmberRead {
  id: string;
  title: string;
  canonical_text: string;
  /** The 1–3 sentences carrying the piece, each with the speaker's own words; [] on older Embers. */
  key_sentences: KeySentence[];
  /** The speaker's photos the writer placed in the piece; [] when none. */
  photos: EmberPhoto[];
  /** Cover and music; the two pending bits say whether the Ember is whole yet. */
  media: EmberMedia;
  /** The receipt (§0.9): the day and the door of this telling — what the Ember page shows above its title. */
  provenance: EmberProvenance;
  /**
   * The lights this Ember stands at that have answered questions (§4.17,
   * 2026-09-24; all of them since 2026-09-26), busiest first: how many
   * stand there, and the question the light's page opens with. [] when none.
   */
  lights: EmberLight[];
  /**
   * The pages of this Ember's path (§4.2, 2026-10-02): every Ember on a path
   * this one is on, in telling order, this one among them — cover, title, the
   * day. [] unless there are two, and [] on a visitor's Ember.
   */
  path_pages: PathPage[];
}

export interface EmberLight { slug: string; title: string; people_count: number; question: string; }
export interface PathPage { id: string; title: string; cover_url: string; told_at: string | null; }

export interface ConversationEmber extends EmberRead {
  created_at: string;
  after_turn_index: number;
  /**
   * When the arrival ceremony finished in front of its owner (2026-09-12);
   * null until then. The thread draws the card only once presented, and a
   * whole Ember not yet presented is what the ceremony plays for.
   */
  presented_at: string | null;
}

/** "Was this any help?" — how the row left the screen, and what was said (§4.5, 2026-09-15). */
export type HelpAskOutcome = 'answered' | 'dismissed' | 'expired';
export type HelpAskAnswer = 'yes' | 'sort_of' | 'not_really';

/** A whole Ember: its text is there and neither slot is still on its way. */
export function emberIsWhole(e: ConversationEmber): boolean {
  return e.media.header_image_pending !== true && e.media.music_pending !== true;
}

/**
 * The browser's IANA time zone (2026-09-28): rides with each turn, so Ember is
 * told the day and hour where this person is. Nothing when the browser cannot say.
 */
function timezoneField(): { timezone?: string } {
  const zone = browserTimezone();
  return zone ? { timezone: zone } : {};
}

/** The crisis cards' words (edge system-notices.js CRISIS_LINES, by the person's country). */
export interface CrisisLines {
  self_harm: string;
  harm: string;
}

/** Which turns GET /api/conversations/:id reads — see emberApi.getConversation. */
export interface ConversationRead {
  limit?: number;
  before?: number | null;
  after?: number | null;
}

export interface ConversationResponse {
  /** The age check standing on this person (edge age-check.js, 2026-10-27): the thread draws its question after the turns. */
  age_check?: AgeCheck | null;
  /**
   * The words of the crisis cards, in the person's country's lines (2026-10-29, edge system-notices.js CRISIS_LINES):
   * `self_harm` for the card at `crisis_referred_at`, `harm` at `harm_referred_at`. Present when either was placed.
   */
  crisis_lines?: CrisisLines;
  conversation: {
    id: string; status: ConversationStatus; entry_type: string; entry_id: string | null;
    /** The settlement cursor (§6.2): turns after it are the telling not yet written into an Ember. */
    written_through_turn?: number | null;
    /**
     * When the crisis resources were last placed in this conversation
     * (2026-09-17, system-notices.js): the thread draws the card after the
     * last turn written before that moment. Null when never.
     */
    crisis_referred_at?: string | null;
    /** When the lines for a risk of hurting someone else were last placed (2026-10-12, migration 348); the same card, its own words. */
    harm_referred_at?: string | null;
  };
  messages: ConversationMessage[];
  /**
   * The window this read drew (2026-09-17): whether turns older than
   * `messages[0]` exist, and that turn's index — the `before` of the next
   * read back. A forward read (`after`) always says there is no more.
   */
  page: { has_more: boolean; oldest_turn_index: number | null };
  /**
   * A reply is being made for this conversation right now (2026-10-17). With
   * the person's words last and this false, nothing is coming: the thread
   * offers Let Ember reply. Absent from an older edge — read as unknown.
   */
  reply_in_flight?: boolean;
  /** Absent only from an edge older than 2026-09-12. */
  embers?: ConversationEmber[];
  /**
   * The person this conversation belongs to, as the thread draws them beside
   * their turns (2026-09-13): their picture — a guest's visitor row has one
   * too. Absent only from an edge older than that day. `letters_pending`
   * (2026-09-18): the letters waiting on this person — the top bar's count —
   * carried on this poll so the door under the header re-reads the letters
   * when it moves (the share ask lands a minute after the Ember arrives).
   */
  viewer?: { avatar_id: string | null; letters_pending?: number; share_ask?: ConversationShareAsk };
  /** The stretch's photos and the cap (2026-10-19 Riley; PhotoRoom). Absent from an older edge. */
  photos_used?: number;
  photos_limit?: number;
  /** The Embers given in this conversation (§4.9, 2026-09-27); the thread draws a card after `after_turn_index`. */
  reads?: ConversationReadItem[];
  /** How it went for others (§4.22, 2026-10-05): the pages given in this conversation; a card after `after_turn_index`. */
  precedents?: ConversationPrecedentItem[];
  /**
   * A guest's yes to a shared Ember or a room (2026-10-11 拍板): while it waits on their joining, a card to join after
   * the reply that answered it (`joining`); a room given when they joined, the card that says Ember is looking
   * (`looking`). A shared Ember given then is its card in `reads`.
   */
  join_cards?: ConversationJoinCard[];
  /** The cards for meeting people replies brought (2026-10-26); a card after `after_turn_index`. */
  feature_cards?: ConversationFeatureCard[];
  /**
   * The three openers of the silent start's line when it is this conversation's latest word, while nothing has been
   * said in it (2026-10-20, edge moments.js): the thread offers them in its starters' place.
   */
  opening_starters?: string[];
  /**
   * The silent start (2026-10-20, moments_nudge): on the opening read of a home conversation nobody has said
   * anything in, keep the clock — `after_ms` of the page on screen with the field untouched. In the `on` arm the page
   * then asks for Ember's line (nudge); in `off` it only records the moment.
   */
  silent_start?: { arm: 'on' | 'off'; after_ms: number };
  /** A guest's door (2026-10-27 founder): on the opening read; absent for a signed-in person. */
  guest_door?: GuestDoor;
}

/**
 * A guest's door (2026-10-27 founder; edge ember-routes guestDoorFor, guest-telling-quota.js): how many of their own
 * messages this browser has said, the count at which the page invites them to join and the one at which the door
 * stands where they type, whether it stands now (false through the hour after a risk was read in their words), and
 * whether the browser belongs to an account that signed out — the door then says sign in.
 */
export interface GuestDoor {
  said: number;
  invite_at: number;
  door_at: number;
  open: boolean;
  sign_in: boolean;
}

export type EntryType = 'home' | 'campaign' | 'node';

/**
 * A voice telling's finalize (the Result page reads it) runs the two
 * extraction passes (story → the Ember, questions → the Request candidates)
 * independently, so one half can fail while the other lands. `ember_error` / `request_candidates_error` are plain-English reasons
 * to show as-is on that half's surface; null means that half is fine.
 */
export interface FinalizeResponse {
  status: 'finalizing' | 'finalized' | 'needs_more_context' | 'failed';
  matching_status: MatchingStatus | null;
  requires_auth: boolean;
  result_url: string | null;
  poll_after_ms: number | null;
  ember_error: string | null;
  request_candidates_error: string | null;
  /**
   * Only on `needs_more_context`: what the story pass said it still needs,
   * in its own words. Empty when the model named nothing.
   */
  missing_information?: string[];
}

export interface ResultEmber {
  id: string;
  slug?: string;
  title: string;
  canonical_text: string;
  status: 'ready' | 'published' | 'private';
  /**
   * false = the Story Writer flagged the written piece unshareable (§6.3):
   * it lives as the owner's private Ember and the page offers no share door.
   */
  shareable: boolean;
  published_at: string | null;
  matching_status: MatchingStatus;
  media?: EmberMedia;
  /**
   * Owner-only impact summary — the Result page renders an entry into
   * /me/embers/:id, where the full resonance stream lives.
   */
  impact: {
    resonance_count: number;
    gratitude_count: number;
    unread_count: number;
  };
  /** The two v3.0 sharing consents as they stand (E04 renders from these). */
  allow_existing_matching: boolean;
  allow_future_matching: boolean;
  /** §4.18: the hand-off link this Ember has out (`/e/<token>`), or null. */
  handoff_path?: string | null;
}

/**
 * A letter (§4.9; edge letters.js): what is waiting on this person, on one
 * row — the ask whether Ember may let people read an Ember of theirs, a
 * note from someone who read it, the teller's words back to a reader. The
 * server decides; the web only shows.
 */
export type LetterKind = 'share_ask' | 'read_note' | 'read_reply'
  // Ask people (§4.19, 2026-09-20): a question sent to this person; answers back to the asker.
  | 'question_asked' | 'answers_arrived' | 'answer_mattered'
  // Rooms (§4.21, 2026-09-25): the invitation to a room, the same to everyone in the held group.
  | 'room_invite';

/**
 * Sharing an Ember (v4.0 §4.9 rewritten, 2026-09-27 拍板): after an Ember is
 * written, a pass reads it; when it holds something that could genuinely
 * help another person, Ember asks the teller whether it may let people it
 * can help read it. The ask waits until answered: yes / no (not this one)
 * / never (the account switch goes off). `match_id` is the Ember's id.
 */
export interface ShareAskRow {
  match_id: string;
  role: 'share';
  letter: LetterKind | null;
  ember: { id: string; title: string };
  /** What Ember saw shining in it, in Ember's own words. */
  value: string;
  /** What Ember said when it asked — the voiced turn's words; the letter's body on the paper. */
  asked_line?: string;
  /** Ember has already said the ask into the conversation as a turn. */
  voiced: boolean;
  answer: 'yes' | 'no' | null;
}

/** One sticky note on a read, from either side. `seen`: the other side has looked at it. */
export interface ReadNote {
  id: string;
  by: 'reader' | 'owner';
  text: string;
  at: string;
  seen: boolean;
}

export type ReadState = 'offered' | 'accepted' | 'opened' | 'closed';

/**
 * An Ember given to a reader (§4.9): one read row seen from one side —
 * `read_reader` (the Ember someone let this person read) or `read_owner`
 * (a person Ember let read this owner's Ember). The two sides leave each
 * other notes; a new note is the other side's letter. `match_id` is the
 * read's id.
 */
export interface ReadRow {
  match_id: string;
  role: 'read_reader' | 'read_owner';
  letter: LetterKind | null;
  read_id: string;
  ember: { id: string; title: string };
  value: string;
  state: ReadState;
  accepted_at: string | null;
  opened_at: string | null;
  closed_at: string | null;
  notes: ReadNote[];
}

/** What the reader reads at /read/:id (GET /api/reads/:readId). */
export interface ReadPageResponse {
  read: { id: string; opened_at: string | null; accepted_at: string | null };
  ember: EmberRead & { language: string };
  provenance: EmberProvenance | null;
  /** What Ember saw in it. */
  value: string;
  notes: ReadNote[];
}

/** An Ember given in a conversation, as the thread draws its card after the turn that gave it. */
export interface ConversationReadItem {
  id: string;
  ember_id: string;
  title: string;
  value: string;
  cover: string | null;
  state: ReadState;
  after_turn_index: number;
  opened_at: string | null;
}

/**
 * How it went for others (§4.22, 2026-10-05): a page Ember put in front of
 * this person while they stood in an uncertainty — a choice between ways
 * forward, a question they keep returning to, a stretch of days they cannot
 * see across — written from the paths of other people here who stood in it.
 * The thread draws its card after the reply that gave it.
 */
export type PrecedentKind = 'choice' | 'question' | 'stretch';
export type PrecedentReaction = 'yes' | 'a_little' | 'no';
export interface ConversationJoinCard {
  what: 'shared_ember' | 'room';
  state: 'joining' | 'looking';
  after_turn_index: number;
}
/**
 * A card for meeting people a reply brought (2026-10-26 founder, New friends; edge friends.js cardBrought): a new friend,
 * rooms by topic, or one topic room Ember invited them into — drawn after that reply; the card is how they take it up.
 * Groups (2026-11-03; edge peer-groups.js cardFacts): `group` — Ember wrote up a post for them in a group open to them;
 * `group_post` — Ember asked them to answer one post. Either is `gone` (drawn as nothing) once it no longer stands.
 */
export interface ConversationFeatureCard {
  what: 'friends' | 'rooms' | 'room' | 'group' | 'group_post';
  room_id?: string | null;
  after_turn_index: number;
  /** A room card's room as it stands now; 'gone' when it no longer takes people. */
  topic?: string;
  status?: 'forming' | 'open' | 'closed' | 'gone';
  present?: number;
  space?: number;
  /** A room card's room is one they are in: it opens as theirs, full or not. */
  mine?: boolean;
  /** A group card's group (both kinds), its name, and for `group` whether they joined it. */
  group_id?: string;
  name?: string;
  joined?: boolean;
  /** A `group_post` card: the post, as the group shows it, and whether they answered it. */
  post_id?: string;
  kind?: GroupPostKind;
  question?: string | null;
  excerpt?: string;
  author?: GroupFace;
  answered?: boolean;
  gone?: boolean;
}
export interface ConversationPrecedentItem {
  id: string;
  title: string;
  lede: string;
  kind: PrecedentKind;
  after_turn_index: number;
  opened_at: string | null;
}
/** The page itself (/how-it-went/:id), its person's only. `ember` is the door to a teller's own words: only for a registered reader, only where the teller said yes to sharing. */
export interface HowItWentPage {
  id: string;
  title: string;
  lede: string;
  kind: PrecedentKind;
  created_at: string;
  opened_at: string;
  reaction: PrecedentReaction | null;
  registered: boolean;
  entries: Array<{
    heading: string;
    body: string[];
    told: { times: number; days: number } | null;
    ember?: { id: string; title: string; read_id: string | null };
  }>;
}

/** The share ask waiting on the person a conversation belongs to. */
export interface ConversationShareAsk {
  ember_id: string;
  title: string;
  value: string;
  voiced: boolean;
  /** The assistant turn Ember voiced the ask in, when it did. */
  voiced_turn: number | null;
}

/**
 * Ask people (v4.0 §4.19, 2026-09-20): a question Ember drafted from one
 * person's conversation and, on their yes, carried to a few others. The
 * server's rows ride the same list as the knocks (GET /api/knocks
 * `questions`) and the same letter rule.
 */
export type QuestionStance = 'lived' | 'seen' | 'perspective';
export interface QuestionAnswer {
  id: string;
  text: string;
  stance: QuestionStance | null;
  at: string;
  /** Asker rows only: whether the asker has looked at it. */
  seen?: boolean;
  /** Asker rows and the answerer's own: the asker said this one mattered. */
  mattered?: boolean;
  /** The answerer's own only: an operator took it down after a report; no one else sees it now (2026-09-23). */
  removed?: boolean;
}
export interface QuestionText {
  id: string;
  text: string;
  context: string;
  /** closed (2026-10-07): Ember stopped carrying it — no new answers; the given ones stay. */
  status: 'offered' | 'declined' | 'open' | 'closed';
  offered_at: string | null;
  sent_at: string | null;
  /** Asker rows: how many people it went to. */
  invited?: number;
  /** The asker withdrew it: no new answers; the given ones stay. */
  withdrawn_at: string | null;
}
/** The asker's side: their open question and every answer so far. */
export interface AskerQuestion {
  match_id: string;
  role: 'asker';
  letter: LetterKind | null;
  question: QuestionText;
  answers: QuestionAnswer[];
}
/** An answerer's side: the question, the others' answers, their own if given. */
export interface AnswererQuestion {
  match_id: string;
  role: 'answerer';
  letter: LetterKind | null;
  question: QuestionText;
  answers: QuestionAnswer[];
  mine: QuestionAnswer | null;
  passed: boolean;
}
export type QuestionRow = AskerQuestion | AnswererQuestion;

/**
 * A room invitation as a letter (§4.21, 2026-09-25): the row GET /api/knocks
 * carries in `rooms[]` while the invitation waits — the tree's envelope, the
 * talk page's door line and the paper's two buttons read it like any letter.
 * Answered on the paper it is patched in place (`answer`, `letter` null)
 * and is gone from the next read; the room itself lives on /rooms/:id and
 * in the Chats list (2026-10-20).
 */
export interface RoomInviteRow {
  match_id: string;
  role: 'member';
  letter: LetterKind | null;
  answer: 'yes' | 'no' | null;
  room: {
    id: string; name: string; fork: string;
    /** This person's seat: the name and picture they have in this room, how the invitation came. */
    my_name: string; avatar: string; via: 'intent' | 'availability' | 'standby';
    invited_at: string | null; grows_to: number; join_window_hours: number; days: number;
    /** The room is scheduled at this many yeses (three since 2026-09-29): with `yeses`, whether this person's yes is the one, or more are still to come. */
    opens_on_yeses: number;
    /** How long a name here can be: the name is theirs to change on the paper (2026-10-20). */
    name_max_chars: number;
    /** The hour the room opens at, once the third yes has set it (2026-10-20: Ember sets it; nothing is proposed before); ISO, drawn on this browser's clock. */
    opens_at: string | null;
    /** The others who have already said yes and are waiting on this person (2026-09-28): the door line and the paper say so. */
    yeses: RoomYes[];
  };
}

/** A seat whose yes is already given: the name and picture that person has in the room. */
export interface RoomYes { name: string; avatar: string }

export type Knock = ShareAskRow | ReadRow | QuestionRow | RoomInviteRow;

/**
 * Rooms (v4.0 §4.21, 2026-09-25): a few people Ember thought might feel less
 * alone together, with Ember present. The room's page (GET /api/rooms/:id);
 * the web lists the person's rooms in the Chats list (GET /api/me/chats,
 * 2026-10-20) — GET /api/me/rooms stays for the apps' older versions.
 */
export type RoomStatus = 'forming' | 'open' | 'closed' | 'lapsed';
/** One emoji on a line as this viewer sees it (小屋留人需求 需求四, 2026-10-19): `by` the room names that put it there (a long press on its capsule), `mine` whether one of them is this person. */
export interface RoomReaction {
  emoji: string;
  count: number;
  mine: boolean;
  by: string[];
}
export interface RoomLine {
  id: string;
  seq: number;
  kind: 'human' | 'host' | 'system';
  text: string;
  at: string | null;
  reason: string | null;
  author: string | null;
  avatar: string | null;
  mine: boolean;
  /** The emoji on this line (需求四); absent on a page from before 2026-10-19 and on a line just arrived over Stream. */
  reactions?: RoomReaction[];
}
export interface RoomMember {
  name: string;
  avatar: string;
  me: boolean;
  /** The line on their seat (2026-10-04 二次拍板): the sentence approved at the door, read by everyone in the room. */
  intro: string | null;
  last_visit_at: string | null;
  /** How far this member has read (需求三, 2026-10-19): their avatar sits on the last line they read. Null for the viewer and for someone they blocked. */
  read_seq?: number | null;
}
export interface RoomPageResponse {
  room: {
    id: string; fork: string; name: string; status: RoomStatus; opened_at: string | null; locked_at: string | null; closes_at: string | null; closed_at: string | null;
    kept_until?: string | null; last_human_line_at?: string | null;
    opens_on_yeses: number; grows_to: number; join_window_hours: number; days: number; name_max_chars: number; line_max_chars: number;
    /** The hour the room opens at, once the third yes has set it (2026-10-20). */
    opens_at: string | null;
    /** The others who have already said yes and are not seated yet (the invitation says they are waiting; 2026-09-28); with `opens_on_yeses`, whether this yes is the one that sets the hour. */
    yeses: RoomYes[];
    /** What kind of room (2026-10-26, New friends): group (formed by a shared situation, until 10-26) | topic | pair. Absent from an older edge. */
    kind?: RoomKind;
    /** The topic a topic room was put up on; null for the other kinds. */
    topic?: string | null;
  };
  me: {
    name: string; avatar: string; via: 'intent' | 'availability' | 'standby'; answer: 'yes' | 'no' | null;
    /** The line on this person's seat (2026-10-04 拍板, 二次拍板 the same day: on the seat, not in Ember's mouth): read at the door first, theirs to change; null when the judge wrote none. */
    intro: string | null;
    seen: boolean; joined: boolean; left: boolean; took_lines: boolean; removed: boolean; muted_until: string | null; felt: string[] | null; read_seq: number;
    /** Ember's note to this person, with the page (the room's last day, 2026-10-17): shown as the room_note card. */
    note?: { text: string; after_seq: number } | null;

    /** The lines this person reported (hidden for them): `lines` already omits them; a thread read live from Stream drops them itself. */
    hidden_lines: string[];
    /** The names, in this room, of the people this person blocked (App Store 1.2, 2026-09-30): `lines` omits theirs; the live thread drops them by name. */
    blocked: string[];
  };
  members: RoomMember[];
  /** The emoji a line can carry, the first three the quick bar a long press opens (需求四; ROOM.REACTIONS, sent with the page). Absent before 2026-10-19. */
  reaction_set?: string[];
  /** How many of reaction_set the quick bar shows (5 since 2026-10-19). */
  reaction_quick?: number;
  people: number;
  lines: RoomLine[];
}
/** The way into a room's live thread (2026-09-29; edge stream.js): this person's identity in this one room on Stream, a token for it, the channel to watch. */
export interface RoomStreamDoor {
  api_key: string;
  user_id: string;
  token: string;
  channel_type: string;
  channel_id: string;
  expires_at: string;
}
export type RoomFelt = 'say' | 'understood' | 'accepted' | 'fun' | 'none';

/**
 * The Chats list (2026-10-20; GET /api/me/chats, edge ember/chats.js): every
 * conversation the person has, already in the one order every client draws —
 * Ember, the pinned rooms, the rooms in progress, invitations and yeses still
 * waiting, the rooms Ember is looking for, the closed. `clock.zone` is the
 * person's own clock: the rows' times and the chat's night are read on it.
 */
export type ChatKind = 'ember' | 'room' | 'invite' | 'waiting' | 'looking' | 'closed';
/** A conversation's newest line: who said it ('You' for the person, 'Ember', or a room name), and what. */
export interface ChatLast { who: string; text: string; at: string }
export interface ChatEmberRow {
  key: 'ember';
  kind: 'ember';
  conversation_id: string | null;
  name: string;
  last: ChatLast | null;
  preview: string;
  unread: number;
  at: string | null;
}
export interface ChatRow {
  /** room:<id> | invite:<id> | waiting:<id> | looking:<intent> — stable while the row is what it is. */
  key: string;
  kind: Exclude<ChatKind, 'ember'>;
  room_id: string | null;
  /** The room's kind (2026-10-26, New friends): a pair's introduction and its wait open on /friends/intro/:id. Absent from an older edge. */
  room_kind?: RoomKind;
  intent_id: string | null;
  /** English, server-written; "Finding a room for you" on a looking row. */
  name: string;
  /** The others seated: the room's picture. */
  people: Array<{ name: string; avatar: string }>;
  pinned: boolean;
  pinnable: boolean;
  muted: boolean;
  unread: number;
  /** The newest line a room in progress shows; null for every other kind. */
  last: ChatLast | null;
  /** The row's second line, ready to show. */
  preview: string;
  at: string | null;
}
export interface ChatsResponse {
  clock: { zone: string; source: 'location' | 'clock' | 'phone' | 'fallback' };
  ember: ChatEmberRow;
  chats: ChatRow[];
  /**
   * Whether this person may meet people (2026-10-26 founder): the homepage's Meet people sign is theirs only then; and
   * whether the home opens the Meet people intro by itself (2026-10-27: once a person, never in an acute period); and
   * whether the sign says "new!" (2026-10-28: from the intro until they have been in the page).
   */
  friends?: { eligible: boolean; intro?: boolean; fresh?: boolean };
}

/**
 * New friends (2026-10-26 founder; edge friends.js): a new friend (1-on-1, one request at a time) and rooms by topic,
 * both on the New friends page. A person who may not take part gets `{ eligible: false }` and nothing else.
 */
export type RoomKind = 'group' | 'topic' | 'pair';
export type FriendRequestStatus = 'looking' | 'found' | 'met' | 'stopped';
export type FriendPronoun = 'she' | 'he' | 'they';
/** Who they would like to meet (2026-10-28 founder): anyone, the first answer, or only women or only men. */
export type FriendWant = 'anyone' | 'women' | 'men';
export interface FriendCard {
  want?: FriendWant;
  gender?: FriendPronoun | null;
  topics: string[];
  name: string;
  line: string;
  avatar: string;
  texting?: boolean;
}
export interface FriendRequest {
  id: string;
  status: FriendRequestStatus;
  card: FriendCard;
  room_id: string | null;
  since: string | null;
  updated_at: string | null;
  found_at: string | null;
  met_at: string | null;
  stopped_at: string | null;
}
export interface TopicRoomRow { room_id: string; topic: string; name: string; present: number; space: number; status?: 'forming' | 'open' }
export interface FriendsPage {
  eligible: boolean;
  /** This person as their card starts: the clock the other side sees (the server's), and their own picture. */
  me?: { zone: string | null; avatar: string | null };
  /** The card of their latest request, whatever became of it (2026-10-26): a new request starts from it. */
  last_card?: FriendCard | null;
  /** The request that stands (looking, or found with the introduction's two seats), or null. */
  friend?: (FriendRequest & { intro: { room_id: string; them: FriendIntroThem; me: FriendIntroMe } | null }) | null;
  rooms?: {
    /** Places held — topics up and rooms sat in — of `cap`; both null for one of Ember's own (no limit). */
    held: number | null;
    cap: number | null;
    /** Topic rooms of theirs still waiting for people. */
    up: Array<{ room_id: string; topic: string; name: string; present: number; opens_on: number; since: string | null }>;
    /** Rooms they sit in now (topic rooms, and rooms by situation running their course). */
    sitting: Array<{ room_id: string; kind: RoomKind; topic: string | null; name: string; people: Array<{ name: string; avatar: string }>; unread: number }>;
  };
  /** Topic rooms others have up: waiting for people (with how many), open with space. */
  browse?: { waiting: TopicRoomRow[]; open: TopicRoomRow[] };
  earlier?: {
    met: Array<{ request_id: string; room_id: string | null; room_status: RoomStatus | null; at: string | null; them: { name: string; avatar: string } | null; topics: string[] }>;
    stopped: Array<{ request_id: string; at: string | null; card: FriendCard }>;
  };
}
export interface FriendIntroThem {
  name: string; avatar: string; line: string; topics: string[]; zone: string | null; texting: boolean; answered: boolean;
  /** Their first line, once the chat is open. */
  opener?: string | null;
}
export interface FriendIntroMe {
  name: string; avatar: string; answer: 'yes' | 'no' | null; opener: string | null;
  /** Whether Ember is looking for a new friend for this person now. */
  looking?: boolean;
}
export interface FriendIntroPage {
  room_id: string;
  status: RoomStatus;
  me: FriendIntroMe;
  them: FriendIntroThem;
  why: {
    hunch: string;
    mine: string[];
    both: string[];
    theirs: string[];
  };
  easy: string[];
  openers: string[];
  asks: string[];
  expires_at: string | null;
}
export interface FriendCheckinPage {
  room_id: string;
  status: RoomStatus;
  opened_at: string | null;
  them: { name: string; avatar: string };
  me: { name: string; avatar: string };
  lines: number;
}
export interface TopicRoomPreview {
  room: { room_id: string; topic: string; name: string; status: 'forming' | 'open'; present: number; space: number; opens_on: number; people: Array<{ name: string; avatar: string; intro: string | null }> };
  /** The line Ember drafts for this person in this room, theirs to change; empty when their places are all held. */
  line: string;
  /** The name and picture they would have in this room (none when their places are all held). */
  seat?: TopicSeat;
  /** All their places for rooms are held (not one of Ember's own): the page says so before anything else. */
  full?: boolean;
  cap?: number;
}

/** A seat in a topic room before it is taken (2026-10-26 founder): the name and picture it comes with, and the room's id. */
export interface TopicSeat {
  room_id: string;
  name: string;
  avatar: string;
}

/**
 * Groups (2026-11-03 founder; edge peer-groups.js): Meet people's third way. A group is about one kind of thing people
 * live through; it is open to the people we marked for it, who join without approval and see every post. A post is a
 * question (a question and a little background) or a share (one piece of text). In a group each person is a room name
 * and picture (`GroupFace`); `ember` is Ember itself, under its own name and flame.
 */
export interface GroupFace { name: string; avatar: string | null; ember: boolean }
/**
 * A post's kind (2026-11-04: Ember starts things in the group itself): a member's question or share; Ember also asks a
 * question, starts a chain (`question` its opening line; each member adds one short line — the post's top-level answers)
 * or puts up a poll (`question` the poll's question, `poll` its options). Ember's own posts have an empty `body`.
 */
export type GroupPostKind = 'question' | 'share' | 'chain' | 'poll';
/** What a member puts up from the page (a chain and a poll are Ember's). */
export type GroupMemberPostKind = 'question' | 'share';
/**
 * A poll as this person sees it: votes are anonymous, only counts are shown, and only once they voted — `count` is null
 * until then; `mine` the index of their option (null: not voted); `voters` how many voted.
 */
export interface GroupPoll { options: Array<{ text: string; count: number | null }>; mine: number | null; voters: number }
/** One emoji on a post or an answer (most used first); `mine` = theirs — one per person per item. */
export interface GroupReaction { emoji: string; count: number; mine: boolean }
/** The group's emoji: the whole set, and how many of it the quick bar shows first. */
export interface GroupEmoji { all: string[]; quick: number }
export interface GroupRow {
  id: string;
  name: string;
  about: string;
  /** Which drawing the group wears (web group-art), or null for the plain tinted ground. */
  art: string | null;
  joined: boolean;
  /** People who joined. */
  people: number;
  /** Posts in the last 7 days. */
  week: number;
  /** Posts by others since they joined that they have not opened. */
  fresh: number;
}
export type GroupsList = { open: false } | { open: true; groups: GroupRow[]; locked: Array<{ id: string; name: string }> };
export interface GroupPost {
  id: string;
  group_id: string;
  kind: GroupPostKind;
  question: string | null;
  body: string;
  at: string | null;
  author: GroupFace & { me: boolean };
  /** Top-level answers only; `first_answer` the first of them, `answered_by` the faces of who gave them. */
  answer_count: number;
  first_answer: { text: string; author: GroupFace } | null;
  answered_by: GroupFace[];
  reactions: GroupReaction[];
  /** How many others opened it. */
  seen: number;
  /** A poll's options and counts (`kind: 'poll'` only). */
  poll?: GroupPoll;
  /**
   * A chain's lines, in time order, on its card in the feed too (`kind: 'chain'`; a post's own page carries every post's
   * answers): the card shows the first few, and Add yours only to someone whose line is not among them.
   */
  answers?: GroupAnswer[];
}
/** A reply under an answer (one level only). */
export interface GroupReply { id: string; text: string; at: string | null; author: GroupFace & { me: boolean }; reactions: GroupReaction[] }
/** A top-level answer, its replies in time order under it. */
export interface GroupAnswer extends GroupReply { replies: GroupReply[] }
export interface GroupPage {
  /** `people` who joined; `week` posts in the last 7 days; `faces` a few of the others in it (joined only). */
  group: { id: string; name: string; about: string; art: string | null; people: number; week: number; joined: boolean; me: GroupFace; faces?: GroupFace[] };
  /** Every post, newest first; [] until they join. */
  posts: GroupPost[];
  emoji: GroupEmoji;
}
export interface GroupPostPage {
  group: { id: string; name: string; art: string | null; me: GroupFace };
  /** `answers`: the top-level answers in time order. */
  post: GroupPost & { answers: GroupAnswer[] };
  emoji: GroupEmoji;
}
export interface GroupDraft { kind: GroupMemberPostKind; question: string; body: string }
/** What Ember looks at before it goes up: a post, or an answer (a reply, a chain's line). */
export type GroupWritingKind = GroupMemberPostKind | 'answer';
/** What the check would change: a detail that would let someone be recognised, or a line that goes at someone. */
export interface GroupChange { why: 'identifies' | 'hurts'; said: string; now: string }
/** The check's answer: no changes — it may go up as it is; else the whole of it as the check would have it. */
export interface GroupCheck {
  changes: GroupChange[];
  suggested?: { question?: string; body?: string; text?: string };
}

export interface KnocksResponse {
  /** The retired knock's two lists, kept empty for the native app's legacy contract. */
  seeker: never[];
  owner: never[];
  /** The share asks waiting on this person (§4.9, 2026-09-27). */
  shares?: ShareAskRow[];
  /** The reads this person is a side of, as reader or as owner. */
  reads?: ReadRow[];
  questions?: { asker: AskerQuestion[]; answerer: AnswererQuestion[] };
  /** The room invitations waiting (§4.21); absent for a guest and for the native app's legacy contract. */
  rooms?: RoomInviteRow[];
}

export interface ResultResponse {
  phase: 'finalizing' | 'finalized' | 'needs_more_context' | 'qa_rejected' | 'story_failed' | 'failed';
  matching_status: MatchingStatus;
  ember: ResultEmber | null;
  /** Owner-only, process-local prose while QA has not accepted an Ember yet. */
  draft: {
    title: string;
    canonical_text: string;
    media: EmberMedia;
  } | null;
  /** §4.17 the lights this telling stood at (seals under the title). */
  nodes?: TreeNode[];
  /** pending while the life-node pass is still running behind the Result. */
  nodes_status?: 'pending' | 'done';
  /** Story pass failed: no Ember, and this is why (see FinalizeResponse). */
  ember_error: string | null;
  missing_information?: string[];
}

/**
 * Where one telling is on its way to becoming an Ember (§4.12). `writing` /
 * `slow` / `stalled` are in flight and have no Ember row yet; `ready` is
 * written but not decided; `published` is live; `private` is kept by its
 * owner, out of the matching pool unless they later share it after all.
 */
export type MeEmberState =
  | 'writing' | 'slow' | 'stalled'
  | 'ready' | 'published' | 'private'
  | 'needs_more_context' | 'below_floor' | 'failed';

export interface MeEmberItem {
  key: string;
  state: MeEmberState;
  ember_id: string | null;
  conversation_id: string | null;
  interview_id: string | null;
  title: string;
  /**
   * The Ember's first words, cut at about two lines (§4.12, 2026-09-04 second
   * pass): the row reads as a page of the person's own writing. Empty on the
   * rows that never became an Ember.
   */
  excerpt: string;
  /**
   * The §0.9 receipt — how and on which day this was told — the same facts the
   * owner page carries in its header. Null on rows with no Ember, and on an
   * Ember whose source conversation is gone.
   */
  provenance: EmberProvenance | null;
  published_at: string | null;
  activity_at: string | null;
  /** Where the card goes when tapped; null while nothing exists to open yet. */
  href: string | null;
  /**
   * Only on `needs_more_context`: what the story pass said the telling still
   * needs, in its own words. Empty on every other state, and empty when the
   * model named nothing.
   */
  missing_information: string[];
  private_resonance_count: number;
  gratitude_count: number;
  unread_impact_count: number;
  /**
   * How many people have read this Ember (§4.9, 2026-09-24): the count of
   * people Ember let read it, the ones who are still reading and the ones
   * who have closed it. 0 until someone does; no names, ever.
   */
  read_count: number;
  media: EmberMedia;
}

export interface ImpactRecord {
  resonance_id?: string;
  anonymous_display_name: string;
  created_at: string;
  gratitude_text: string | null;
  /** §0.9 receipt: whether the owner already tapped "Let them know you got it". */
  acknowledged?: boolean;
  /** The owner's answer, when the receipt came with words (2026-09-09). */
  acknowledged_text?: string | null;
  /**
   * The Request the resonance happened under. Null when it came from the
   * public Ember page (no Request there) or the Request was taken down.
   */
  request: { id: string; canonical_text: string } | null;
}

/** Profile header (§4.10, 2026-09-01 redesign): identity + tenure + counts. */
export interface MeSummaryResponse {
  avatar_id: string | null;
  line: string | null;
  /** "March 2026" — the same spelling the person page uses. */
  since_month: string;
  embers_told: number;
  mattered_total: number;
}

const DEV_ME_SUMMARY: MeSummaryResponse = {
  avatar_id: 'red-fox:golden-hour', line: 'Keeping a small light on.',
  since_month: 'March 2026', embers_told: 12, mattered_total: 27,
};

/** The legally sensitive categories an Ember's account can be judged into (edge account_structure). */
export type BackgroundSensitiveCategory =
  | 'health'
  | 'sex_life_or_sexual_orientation'
  | 'transgender_or_nonbinary_status'
  | 'religion'
  | 'race_or_ethnicity'
  | 'citizenship_or_immigration';

/** One read of the owner's Ember, on the owner's page: who (never a name), where it stands, the notes. */
export interface OwnerRead {
  id: string;
  ember_id: string;
  title: string;
  value: string;
  cover: string | null;
  /** The reader's avatar key (an animal in a place, never a photo) — the owner's row of readers (2026-09-30). Null for a reader with none. */
  reader_avatar?: string | null;
  state: ReadState;
  offered_at: string | null;
  accepted_at: string | null;
  opened_at: string | null;
  closed_at: string | null;
  notes: ReadNote[];
}

export interface OwnerEmberDetailResponse {
  ember: EmberRead & {
    slug?: string;
    status: 'ready' | 'published' | 'private';
    published_at: string | null;
    /** v3.0 S13: the future-matching switch this page can flip. */
    allow_future_matching?: boolean;
    /**
     * Sharing (§4.9, 2026-09-27; shares.js ownerView): whether the pass
     * found something in this Ember worth another person's reading and
     * what it saw, whether Ember has asked, the owner's answer (null = not
     * asked or not yet answered), and every read given — each with its notes. Null on an
     * edge without the share service.
     */
    share: {
      worth: boolean;
      value: string;
      /** Whether Ember has asked them about it yet. */
      asked: boolean;
      share_consent: 'yes' | 'no' | null;
      share_answered_at: string | null;
      reads: OwnerRead[];
    } | null;
    /** How many people have read it (§4.9) — the number under "Who has read it". */
    read_count: number;
    background_enabled: boolean;
    /**
     * The legally sensitive category this Ember's account was judged into, or
     * null (2026-09-14 晚). For such an Ember the default-on switch is not
     * consent: it reads as unanswered until the owner turns it on themselves,
     * and `background_consented` says whether they have.
     */
    background_sensitive?: BackgroundSensitiveCategory | null;
    background_consented?: boolean;
    /** The telling behind it; the address of the owner-only transcript page. */
    source_conversation_id?: string | null;
    /** §4.18: the hand-off link this Ember has out (`/e/<token>`), or null. */
    handoff_path?: string | null;
  };
  impact: ImpactRecord[];
}

/** S15 person page (§4.16): P01 header + the viewer-scoped readable Embers. */
export interface PersonPageResponse {
  presence: {
    avatar_id: string | null;
    line: string | null;
    since_month: string;
    is_self: boolean;
  };
  embers: Array<{
    ember_id: string;
    title: string;
    published_at: string | null;
    media?: EmberMedia;
    entry:
      | { kind: 'direct'; conversation_id: string }
      // The owner's own list (is_self): an Ember that has not reached the
      // viewer by any door, because the viewer is its teller.
      | { kind: 'own' };
  }>;
}

/** Your space (§12.8): the owner's presence + open line candidates. */
export interface MePresenceResponse {
  avatar_id: string | null;
  presence_token: string | null;
  line: string | null;
  suggestions: Array<{ id: string; lines: string[]; created_at: string }>;
}

export interface NotificationSummary {
  unread_new_ember_count: number;
  unread_answer_invitation_count?: number;
  /** Letters about an Ember (§4.9) waiting on this person — the share ask, a reader's note, the teller's words back (the Embers tab). */
  pending_knock_count: number;
  /** The Ask people letters (§4.19) waiting on this person — the Questions tab (2026-09-24). */
  pending_question_count?: number;
  /** Ember's weekly letters (§4.20) not yet opened in the notebook — the Letters tab. */
  unread_letter_count?: number;
  /** Rooms (§4.21): invitations waiting and rooms with unread lines — counted in the total (the Chats list since 2026-10-20). */
  pending_room_count?: number;
}

/**
 * One of Ember's weekly letters (v4.0 §4.20, 2026-09-23): once a week Ember
 * reads the Embers this person wrote since its last letter (2026-10-31) and
 * writes them a letter, or none.
 * `preview` is its first sentence (the list row); `read_at` when the person
 * first opened it in the notebook. Only weeks with a letter are listed.
 */
export interface WeeklyLetter {
  id: string;
  /** The PT letter day on the person's own calendar (YYYY-MM-DD). */
  due_date: string;
  written_at: string;
  /**
   * The letter with brain's three typesetting marks (letter-format.ts parses
   * them): the person's own words in curly quotes followed by [day], the
   * question's paragraph beginning "? ", a movement as FROM [day] “…” / TO
   * [day] “…” lines.
   */
  letter: string;
  /** Which of their letters this is, 1-based. */
  ordinal: number | null;
  /** The PT day the stretch this letter reads began (the letter before, or registration). */
  reads_from: string | null;
  read_at: string | null;
  conversation_id: string | null;
  /** The letter's hand-off link (/l/<token>) once its reader has opened the Send sheet; null before. */
  handoff_path: string | null;
  /** The picture of their path drawn with the letter (2026-11-04), the card's cover; null when its page has none. */
  cover: string | null;
  /** The page of their paths the letter opens on (2026-11-03), on the letter's own read only; null when they had no path. */
  path?: LetterPathPage | null;
}
/**
 * The page of their paths a weekly letter opens on (§4.20, 2026-11-03): written with the letter, that week's. Every
 * path of theirs, the one told most recently first.
 */
export interface LetterPathPage {
  headline: string;
  paths: LetterPath[];
}
export interface LetterPath {
  id: string;
  name: string;
  /** A stop on it was told since the last letter. */
  moved: boolean;
  /** Where they are on it now, and how many other people have stood there. */
  place: { line: string; others: number } | null;
  /** The pull they are caught in there, and how many others, in other lives, feel it. */
  pull: { line: string; others: number } | null;
  /** In order; the last is where they are. */
  stops: LetterPathStop[];
}
export interface LetterPathStop {
  id: string;
  line: string;
  /** Told since the last letter. */
  new: boolean;
  /** Other people who stood in this stop's situation. */
  others: number;
  /** Their Embers written from this stop, as they are now. */
  embers: { id: string; title: string; image_url: string; created_at: string | null }[];
}
/**
 * What a turn is about (2026-09-24, §4.5): the person came into the
 * conversation from their letter's or their Ember's page, through its "Have
 * thoughts about this?" door. `label` is what the composer's chip says; the
 * server is told only kind and id and reads the object itself.
 */
export interface TurnAbout { kind: 'letter' | 'ember'; id: string; label: string }
/** §4.20 the recipient's read of a letter's hand-off link: the letter itself, and its day. */
export interface LetterHandoffResponse {
  letter: { id: string; written_at: string | null; letter: string; plain: string };
}

export interface TellingQuotaResponse {
  /** v3.0: a spent budget asks for inbound-SMS registration, never /auth. */
  requires_registration: boolean;
  /** Legacy alias of requires_registration (one release). */
  requires_auth: boolean;
  /** 2026-10-01: the refusal is a sign-in — this browser's visitor row is bound to an account that signed out. */
  has_account?: boolean;
  /** 2026-10-27: which budget it is — free_telling_used | account_signed_out | guest_messages_used | ''. Absent from an older edge. */
  reason?: string;
  free_tellings: number | null;
  used: number | null;
  remaining: number | null;
}

// ---------------------------------------------------------------------------
// Error helpers — the frontend switches on the §12.7 `error` code, never on
// message strings.
// ---------------------------------------------------------------------------

/**
 * Words of theirs that others would read and that did not go out (the server's review of a topic, a seat's line, a card,
 * a first line — 2026-10-27): which field (`field`, as the server named it) and the sentence the page says; null when the
 * error is something else. `what` names each field as the page speaks of it ("Your line", "A topic").
 */
export function wordsRefused(err: unknown, what: Record<string, string>): { field: string; message: string } | null {
  if (!(err instanceof ApiError) || !err.body || typeof err.body !== 'object') return null;
  const body = err.body as { error?: string; field?: string; resources?: string };
  const field = body.field || '';
  const subject = what[field] || Object.values(what)[0] || 'That';
  if (body.error === 'risk_to_self') return { field, message: `That one stays with you and isn’t going out. ${body.resources || ''}`.trim() };
  if (body.error === 'text_rejected') return { field, message: `${subject} can’t go out as written. Say it another way?` };
  if (body.error === 'moderation_unavailable') return { field, message: 'Couldn’t check that just now. Try again in a moment.' };
  return null;
}

export function apiErrorCode(err: unknown): string {
  if (err instanceof ApiError && err.body && typeof err.body === 'object') {
    const code = (err.body as { error?: unknown }).error;
    if (typeof code === 'string') return code;
  }
  return '';
}

/**
 * The reason a failed request is recorded under (ui.failed `reason`): the
 * §12.7 code when the edge refused with one; else the HTTP status it answered
 * with — `http_502`, or `http_200_unreadable` when the body could not be read
 * (apiFetch throws that for a 2xx too; the ApiError's body is null then);
 * else the browser's own failure with its message (`network_error:Load
 * failed`). Until 2026-09-16 the Settings page recorded every one of these
 * as `load_failed`, and three iOS Safari failures could not be told apart.
 */
export function apiFailureReason(err: unknown): string {
  const code = apiErrorCode(err);
  if (code) return code;
  if (err instanceof ApiError) return `http_${err.status}${err.body === null ? '_unreadable' : ''}`;
  const message = err instanceof Error ? err.message : '';
  return message ? `network_error:${message}` : 'network_error';
}

export function isAuthRequired(err: unknown): boolean {
  return err instanceof ApiError && err.status === 401;
}

export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ik-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------

const enc = encodeURIComponent;

/** The age check (edge age-check.js): the age Ember read in their words, said back in its question. */
export interface AgeCheck {
  said: number | null;
}

/** The answer's reply: on 18 or older, their answer as their line and Ember's reply to it; on under 18, the words to show. */
export interface AgeCheckAnswer {
  ok: boolean;
  answer?: string;
  closed?: boolean;
  text?: string;
  user_turn?: ConversationMessage | null;
  assistant_turn?: ConversationMessage | null;
}

export const emberApi = {
  home(opts: { track?: string | null } = {}): Promise<HomeResponse> {
    return apiFetch<HomeResponse>(`/api/home${opts.track ? `?t=${enc(opts.track)}` : ''}`);
  },

  /** §4.17 — the viewer's own animal, seen from behind under the tree. */
  treeYou(): Promise<TreeYouResponse> {
    return apiFetch<TreeYouResponse>('/api/tree/you');
  },

  /** §4.17 — one window of the tree: centred on a light, on the viewer's own lights, or today's tree. */
  treeView(opts: { focus?: string | null; include?: string[]; size?: number; track?: string | null } = {}): Promise<TreeViewResponse> {
    const q = new URLSearchParams();
    if (opts.focus) q.set('focus', opts.focus);
    if (opts.include?.length) q.set('include', opts.include.join(','));
    if (opts.size) q.set('size', String(opts.size));
    if (opts.track) q.set('t', opts.track);
    const qs = q.toString();
    return apiFetch<TreeViewResponse>(`/api/tree/view${qs ? `?${qs}` : ''}`);
  },

  /** §4.17 — the lights the typed words could mean, most likely first (≤8, above the relevance floor). */
  treeSearch(q: string): Promise<TreeResponse> {
    return apiFetch<TreeResponse>(`/api/tree/search?q=${enc(q)}`);
  },

  /** §4.17 — one light's page. */
  place(slug: string): Promise<PlaceResponse> {
    return apiFetch<PlaceResponse>(`/api/places/${enc(slug)}`);
  },

  /** §4.17 Ask this light — one of the page's questions (`question_id`) or the person's own words (`text`). 404 light too small, 422 not a question, 429 too many. */
  askLight(slug: string, body: { question_id?: string; text?: string }): Promise<LightAnswer> {
    return apiFetch<LightAnswer>(`/api/places/${enc(slug)}/ask`, { method: 'POST', body });
  },

  /** §4.18 the recipient's read of a hand-off link. 404 = no such live link. */
  handoff(token: string): Promise<HandoffResponse> {
    return apiFetch<HandoffResponse>(`/api/handoff/${enc(token)}`);
  },

  /** §4.18 mint (or fetch) this Ember's hand-off link. Owner only; idempotent. */
  mintHandoff(emberId: string): Promise<{ handoff_path: string }> {
    return apiFetch(`/api/embers/${enc(emberId)}/handoff`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  /** §4.18 take the link back: `/e/<token>` stops answering. */
  revokeHandoff(emberId: string): Promise<{ handoff_path: null }> {
    return apiFetch(`/api/embers/${enc(emberId)}/handoff`, {
      method: 'DELETE', idempotencyKey: newIdempotencyKey(),
    });
  },

  tellingQuota(): Promise<TellingQuotaResponse> {
    return apiFetch<TellingQuotaResponse>('/api/telling-quota');
  },

  /**
   * `consent` carries the signed-out visitor's 18+ / Terms acknowledgment. Sent
   * only on the attempt made right after they tick the boxes — the server stamps
   * it on the visitor row, so every later telling from this browser passes
   * without it. A signed-in person never sends it (the account already holds it).
   */
  /** The telling door's two boxes, recorded on the browser's visitor row (§4.5). */
  /** The telling door's two boxes (talk-sheet, the home composer). The
   *  registration sheet and /auth write theirs with door 'register'. */
  visitorConsent(): Promise<{ ok: boolean }> {
    return apiFetch<{ ok: boolean }>('/api/visitor/consent', {
      method: 'POST',
      body: { age_confirmed: true, tos_accepted: true, door: 'talk' },
    });
  },

  /** The age check standing on this account or browser (edge age-check.js, 2026-10-27): the question, or null. */
  ageCheck(): Promise<{ age_check: AgeCheck | null; stopped: boolean }> {
    return apiFetch<{ age_check: AgeCheck | null; stopped: boolean }>('/api/age-check');
  },
  /** Their answer. `under_18` closes what they shared here and deletes it; the reply carries the words to show. */
  answerAgeCheck(answer: 'adult' | 'under_18'): Promise<AgeCheckAnswer> {
    return apiFetch<AgeCheckAnswer>('/api/age-check/answer', {
      method: 'POST',
      // The browser's zone: a guest's closing words carry their country's crisis line.
      body: { answer, ...timezoneField() },
    });
  },

  createConversation(
    entryType: EntryType,
    entryId: string | null,
    consent = false,
    window: Pick<ConversationRead, 'limit'> = {},
  ): Promise<ConversationResponse> {
    return apiFetch<ConversationResponse>('/api/conversations', {
      method: 'POST',
      body: {
        entry_context: { type: entryType, id: entryId },
        ...timezoneField(),
        ...(consent ? { age_confirmed: true, tos_accepted: true } : {}),
        ...(window.limit ? { limit: window.limit } : {}),
      },
      idempotencyKey: newIdempotencyKey(),
    });
  },

  /**
   * The silent start's line (2026-10-20, moments.js): Ember's one more question, landed as the conversation's next
   * turn. A 409 `not_ready` is the ordinary no — someone typed, another device was first — and changes nothing.
   */
  nudge(conversationId: string, tMs: number): Promise<{ assistant_turn: ConversationMessage; opening_starters: string[] }> {
    return apiFetch<{ assistant_turn: ConversationMessage; opening_starters: string[] }>(
      `/api/conversations/${enc(conversationId)}/nudge`,
      { method: 'POST', body: { t_ms: tMs } },
    );
  },

  /**
   * One conversation, read three ways (2026-09-25; the edge's getConversation):
   * `{ limit }` the newest N turns (`before` reaches further back — turns
   * older than that turn_index); `{ after }` only the turns newer than that
   * one; `{}` the whole transcript. The Embers, the viewer and the
   * conversation's state come with every read.
   */
  getConversation(conversationId: string, read: ConversationRead = {}): Promise<ConversationResponse> {
    const qs = new URLSearchParams();
    if (read.limit) qs.set('limit', String(read.limit));
    if (read.before !== undefined && read.before !== null) qs.set('before', String(read.before));
    if (read.after !== undefined && read.after !== null) qs.set('after', String(read.after));
    // The browser's zone: a guest's crisis cards come in their country's words (2026-10-29).
    const zone = browserTimezone();
    if (zone) qs.set('tz', zone);
    const q = qs.toString();
    return apiFetch<ConversationResponse>(`/api/conversations/${enc(conversationId)}${q ? `?${q}` : ''}`);
  },

  /** The arrival ceremony has played for this Ember; stamp it presented. */
  markEmberPresented(conversationId: string, emberId: string): Promise<{ presented_at: string }> {
    return apiFetch<{ presented_at: string }>(
      `/api/conversations/${enc(conversationId)}/embers/${enc(emberId)}/presented`,
      { method: 'POST' },
    );
  },

  /**
   * "Was this any help?" (2026-09-15, §4.5): once the arrival has cleared,
   * ask whether it is time to put the question in this thread. `ask: null`
   * is the ordinary answer — not time (first Ember, then every five and
   * fourteen days, never after two dismissals; the edge keeps the rule).
   */
  openHelpAsk(conversationId: string): Promise<{ ask: { id: string } | null }> {
    return apiFetch<{ ask: { id: string } | null }>(
      `/api/conversations/${enc(conversationId)}/help-ask`,
      { method: 'POST' },
    );
  },
  /** How the row left the screen. `answer` only with `answered`. */
  closeHelpAsk(conversationId: string, askId: string, outcome: HelpAskOutcome, answer?: HelpAskAnswer): Promise<{ closed: boolean }> {
    return apiFetch<{ closed: boolean }>(
      `/api/conversations/${enc(conversationId)}/help-ask/${enc(askId)}/close`,
      { method: 'POST', body: { outcome, answer }, keepalive: true },
    );
  },

  /**
   * One turn, answered as the words come (2026-09-21): `onFace` once with the
   * face this reply wears, then `onDelta` per piece of the reply as Ember
   * writes it; the resolved value is the finished turn — the record — and the
   * page draws that in place of what it streamed. A reply that never streamed
   * arrives whole in the resolved value alone. `reviseTurnIndex` is Try again
   * under a reply that failed: the same words continue the person's own turn
   * already at that index, and Ember answers what is there.
   */
  sendMessage(
    conversationId: string,
    content: string,
    mediaIds: string[] = [],
    {
      signal, onDelta, onFace, reviseTurnIndex, about, photosNotAdded, turnKey, live,
    }: {
      signal?: AbortSignal;
      /** The conversation's channel is joined (live.ts): the reply comes on it, so the POST answers whole, no stream — otherwise the stream, the logged fallback. */
      live?: boolean;
      onDelta?: (delta: string) => void;
      onFace?: (face: MascotFace) => void;
      reviseTurnIndex?: number;
      /** What this turn is about (2026-09-24): the letter or Ember whose door the person came in through. */
      about?: TurnAbout;
      /** Photos the cap refused since the last turn (2026-10-19 Riley): the turn tells Ember they did not get in. */
      photosNotAdded?: number;
      /**
       * The turn's own key (2026-10-21 Riley): minted once per turn when the person sends, the same one on every
       * retry of it — a resend after this page gave up on the wire, Try again. The edge reads the same key in a
       * conversation as the same turn: no second copy of the words, no second reply — the stored turns come back,
       * or the reply still being written comes back whole once it lands (`replayed`).
       */
      turnKey?: string;
    } = {},
  ): Promise<{
    user_turn: ConversationMessage;
    /** Null when the person stopped the reply (stopReply), or a newer turn of theirs overtook it: nothing was kept. */
    assistant_turn: ConversationMessage | null;
    stopped?: boolean;
    /** A newer turn (another device, a text) landed while this reply was written; its reply answers both. */
    overtaken?: boolean;
    /** The turns were made by an earlier request for the same turn (turnKey): nothing new was written or asked. */
    replayed?: boolean;
    /** The age check stands (edge age-check.js): the words were kept and nothing answers them until it is answered. */
    age_check?: AgeCheck | null;
    /** A guest's door after this turn (2026-10-27 founder); absent for a signed-in person. */
    guest_door?: GuestDoor;
  }> {
    const body = {
      content,
      ...(mediaIds.length > 0 ? { media_ids: mediaIds } : {}),
      ...(reviseTurnIndex !== undefined ? { revise_turn_index: reviseTurnIndex } : {}),
      ...(turnKey ? { turn_key: turnKey } : {}),
      ...(about ? { about: { kind: about.kind, id: about.id } } : {}),
      ...(photosNotAdded ? { photos_not_added: { over_cap: photosNotAdded } } : {}),
      ...timezoneField(),
    };
    const path = `/api/conversations/${enc(conversationId)}/messages`;
    if (live) return apiFetch(path, { method: 'POST', body, signal, idempotencyKey: newIdempotencyKey() });
    return apiFetchStream(path, {
      method: 'POST',
      body,
      signal,
      idempotencyKey: newIdempotencyKey(),
      // The fallback (OBSERVE, 2026-10-21): the edge logs it with this reason
      headers: { 'X-Live-Fallback': 'no_subscription' },
      onFrame: (frame) => {
        if (typeof frame.face === 'string' && frame.face) onFace?.(frame.face as MascotFace);
        if (typeof frame.delta === 'string' && frame.delta) onDelta?.(frame.delta);
      },
    });
  },

  /**
   * Stop the reply to the person's turn `afterTurnIndex` (2026-09-03). The
   * edge cancels the brain call and persists nothing — or deletes the reply if
   * it had just landed — so Stop means the same thing whatever the timing.
   * Sent BEFORE aborting the send request: the abort alone told the edge
   * nothing.
   */
  stopReply(conversationId: string, afterTurnIndex: number): Promise<{ stopped: true; turn_index: number; landed: boolean }> {
    return apiFetch(`/api/conversations/${enc(conversationId)}/stop`, {
      method: 'POST',
      body: { after_turn_index: afterTurnIndex },
      idempotencyKey: newIdempotencyKey(),
    });
  },

  /**
   * Upload one picked photo. Raw bytes, not multipart — the edge validates the
   * magic bytes, compresses, and shows the pixels to a vision moderator before
   * anything is stored, so this resolving means the photo is genuinely kept.
   * Send quotes the returned media_id.
   */
  uploadConversationMedia(
    conversationId: string,
    file: File,
    signal?: AbortSignal,
  ): Promise<UploadedMedia> {
    return apiFetch<UploadedMedia>(`/api/conversations/${enc(conversationId)}/media`, {
      method: 'POST',
      // A File is a Blob, so apiFetch forwards the bytes untouched. The header
      // is set explicitly rather than left to the Blob's own type: the edge
      // routes on it, and a file picked with no MIME type would otherwise
      // arrive as a body the raw parser refuses.
      body: file,
      headers: { 'Content-Type': file.type },
      signal,
    });
  },

  /** The room left for photos (2026-10-19 Riley), read as the + opens the picker. */
  getPhotoRoom(conversationId: string): Promise<PhotoRoom> {
    return apiFetch<PhotoRoom>(`/api/conversations/${enc(conversationId)}/photos`);
  },

  /** A tray photo taken out before it was sent (2026-10-19 Riley): the edge deletes it, and its slot is free. */
  removeConversationMedia(conversationId: string, mediaId: string): Promise<PhotoRoom & { deleted: true }> {
    return apiFetch(`/api/conversations/${enc(conversationId)}/media/${enc(mediaId)}`, { method: 'DELETE' });
  },

  /**
   * Voice input: one recorded clip → its words. The edge transcribes and
   * drops the clip; the text lands in the composer, not in the conversation —
   * sending is still the person's move.
   */
  transcribeAudio(
    conversationId: string,
    audio: Blob,
    signal?: AbortSignal,
  ): Promise<{ text: string }> {
    return apiFetch<{ text: string }>(`/api/conversations/${enc(conversationId)}/transcribe`, {
      method: 'POST',
      body: audio,
      headers: { 'Content-Type': audio.type },
      signal,
    });
  },

  getResult(conversationId: string): Promise<ResultResponse> {
    return apiFetch<ResultResponse>(`/api/results/${enc(conversationId)}`);
  },

  resultProgressUrl(conversationId: string): string {
    return `/api/results/${enc(conversationId)}/progress`;
  },

  // ---- Human Presence (§0.9) ----

  /** S15 person page: header for any token holder, Embers scoped to this viewer. */
  personPage(token: string): Promise<PersonPageResponse> {
    return apiFetch(`/api/presence/${enc(token)}`);
  },

  /** Your space (§12.8): the owner's presence + open line candidates. */
  mePresence(): Promise<MePresenceResponse> {
    return apiFetch('/api/me/presence');
  },

  /** One intent per PATCH: {avatar_id} | {line, suggestion_id?} | {dismiss_suggestion_id}. */
  patchMePresence(body: {
    avatar_id?: string;
    line?: string;
    suggestion_id?: string;
    dismiss_suggestion_id?: string;
  }): Promise<MePresenceResponse> {
    return apiFetch('/api/me/presence', {
      method: 'PATCH', body, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** §4.13: the owner's Background switch on one Ember. On is the owner's own yes (`background_consented`). */
  patchEmberControls(
    emberId: string,
    patch: { background_enabled: boolean },
  ): Promise<{ background_enabled: boolean; background_consented: boolean }> {
    return apiFetch(`/api/me/embers/${enc(emberId)}/controls`, {
      method: 'PATCH', body: patch, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** §4.13 `Close it to everyone`: every read of this Ember closes and its hand-off link dies. */
  closeEmberToEveryone(emberId: string): Promise<{ closed: number; link_revoked: boolean }> {
    return apiFetch(`/api/me/embers/${enc(emberId)}/close-all`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  /** §4.13: the owner deletes one Ember (logical; the conversation it came from stays). */
  deleteEmber(emberId: string): Promise<{ ok: true; closed: number }> {
    return apiFetch(`/api/me/embers/${enc(emberId)}`, {
      method: 'DELETE', idempotencyKey: newIdempotencyKey(),
    });
  },

  /**
   * §0.9 receipt: the owner's one tap on a note, with optional words (2026-09-08).
   * Idempotent; a later call with words adds them to the standing receipt.
   */
  acknowledgeResonance(resonanceId: string, text = ''): Promise<{ acknowledged: true; text: string | null }> {
    return apiFetch(`/api/me/resonances/${enc(resonanceId)}/acknowledge`, {
      method: 'POST',
      body: text ? { text } : {},
      idempotencyKey: newIdempotencyKey(),
    });
  },

  myEmbers(): Promise<{ items: MeEmberItem[] }> {
    return apiFetch('/api/me/embers');
  },

  /**
   * Throw a telling away. Offered on the below-floor card, and real: the
   * transcript and any photos sent into it are deleted server-side, not
   * hidden.
   */
  /** `reason` is for the server log only: which door threw the telling away. */
  deleteConversation(
    conversationId: string,
    reason?: 'my_embers' | 'talk_leave',
  ): Promise<{ deleted: true }> {
    return apiFetch(`/api/conversations/${enc(conversationId)}`, {
      method: 'DELETE',
      ...(reason ? { body: { reason } } : {}),
      idempotencyKey: newIdempotencyKey(),
    });
  },

  // --- The letters (§4.9): one list for every kind ---------------------------
  knocks(): Promise<KnocksResponse> {
    return apiFetch('/api/knocks');
  },

  // --- Sharing an Ember (§4.9 rewritten, 2026-09-27) -------------------------
  /** The teller's answer to the ask: yes / no (not this one) / never (the account switch goes off). Signed in or a guest. */
  answerShare(emberId: string, answer: 'yes' | 'no' | 'never'): Promise<{ share_consent: 'yes' | 'no' }> {
    return apiFetch(`/api/shares/${enc(emberId)}/answer`, {
      method: 'POST', body: { answer }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** The owner's later change of mind on the Ember's page: shared on / off. */
  setShareConsent(emberId: string, consent: 'yes' | 'no'): Promise<{ share_consent: 'yes' | 'no' }> {
    return apiFetch(`/api/shares/${enc(emberId)}/consent`, {
      method: 'POST', body: { consent }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** How it went for others (§4.22): the page, its person's only (a guest's by their browser). Opening it is the read. 404 not theirs. */
  howItWent(id: string): Promise<HowItWentPage> {
    return apiFetch(`/api/how-it-went/${enc(id)}`);
  },
  /** The door to a teller's own words from that page: the Ember is given to read (a read, §4.9); registered only. 410 `not_available`. */
  howItWentRead(id: string, emberId: string): Promise<{ read_id: string }> {
    return apiFetch(`/api/how-it-went/${enc(id)}/read`, { method: 'POST', body: { ember_id: emberId }, idempotencyKey: newIdempotencyKey() });
  },
  /** The one question on that page: yes | a_little | no; the latest answer stands. */
  howItWentReact(id: string, answer: PrecedentReaction): Promise<{ reaction: PrecedentReaction }> {
    return apiFetch(`/api/how-it-went/${enc(id)}/reaction`, { method: 'POST', body: { answer }, idempotencyKey: newIdempotencyKey() });
  },

  /** The read page (/read/:id), the reader's only: opening it is the read. 404 not theirs; 410 `closed`. */
  read(readId: string): Promise<ReadPageResponse> {
    return apiFetch(`/api/reads/${enc(readId)}`);
  },

  /** A note on a read, from either side. 422 `text_rejected` (the gate); 400 `too_long` / `empty`. */
  addReadNote(readId: string, text: string): Promise<{ note: ReadNote }> {
    return apiFetch(`/api/reads/${enc(readId)}/notes`, {
      method: 'POST', body: { text }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** This side has looked at the other side's notes: their letter ends. */
  markReadSeen(readId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/reads/${enc(readId)}/seen`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  /** The owner closes one read: the reader can no longer open it. */
  closeRead(readId: string): Promise<{ closed: number }> {
    return apiFetch(`/api/reads/${enc(readId)}/close`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  // --- v4.0 Ask people (§4.19) ---------------------------------------------
  answerQuestion(questionId: string, body: { text: string; stance: QuestionStance | null }): Promise<{ answer: QuestionAnswer }> {
    return apiFetch(`/api/questions/${enc(questionId)}/answers`, {
      method: 'POST', body, idempotencyKey: newIdempotencyKey(),
    });
  },

  passQuestion(questionId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/questions/${enc(questionId)}/pass`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  /** The question page (§4.19): this side's row, or 404. */
  question(questionId: string): Promise<QuestionRow> {
    return apiFetch(`/api/questions/${enc(questionId)}`);
  },

  /** The page or paper shown is the look: the asker's unseen answers, an answerer's unseen "it mattered". */
  markQuestionSeen(questionId: string): Promise<{ ok: true; seen: number }> {
    return apiFetch(`/api/questions/${enc(questionId)}/seen`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  withdrawQuestion(questionId: string): Promise<{ ok: true; withdrawn_at: string }> {
    return apiFetch(`/api/questions/${enc(questionId)}/withdraw`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  markAnswerMattered(questionId: string, answerId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/questions/${enc(questionId)}/answers/${enc(answerId)}/mattered`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  /** Report one answer shown to this person (2026-09-23): hidden for them at once, in front of an operator. */
  reportAnswer(questionId: string, answerId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/questions/${enc(questionId)}/answers/${enc(answerId)}/report`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  ownerEmberDetail(emberId: string): Promise<OwnerEmberDetailResponse> {
    return apiFetch(`/api/me/embers/${enc(emberId)}`);
  },

  notificationSummary(): Promise<NotificationSummary> {
    return apiFetch('/api/me/notifications');
  },

  // ---- Rooms (§4.21, 2026-09-25) ----

  room(roomId: string): Promise<RoomPageResponse> {
    return apiFetch(`/api/rooms/${enc(roomId)}`);
  },
  /** The room's live thread: a Stream identity and token for this room alone (503 while Stream is not there; the page then polls). */
  roomStreamToken(roomId: string): Promise<RoomStreamDoor> {
    return apiFetch(`/api/rooms/${enc(roomId)}/stream-token`, { method: 'POST', body: {} });
  },
  renameInRoom(roomId: string, name: string): Promise<{ ok: true; name: string }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/name`, { method: 'PATCH', body: { name }, idempotencyKey: newIdempotencyKey() });
  },
  /** The sentence Ember says about this person when they come in (2026-10-04 拍板), as they want it said. */
  setRoomIntro(roomId: string, intro: string): Promise<{ ok: true; intro: string }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/intro`, { method: 'PATCH', body: { intro }, idempotencyKey: newIdempotencyKey() });
  },
  /** The answer to a room's invitation: yes to the seat, or no (no hour is asked, 2026-10-20). The browser's zone rides along. */
  answerRoom(roomId: string, accept: boolean): Promise<{ ok: true; answer: 'yes' | 'no'; room_status?: RoomStatus; opened?: boolean; joined?: boolean; waiting?: boolean; scheduled?: boolean; opens_at?: string }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/answer`, { method: 'POST', body: { accept, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  sayInRoom(roomId: string, text: string): Promise<{ line: RoomLine }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/lines`, { method: 'POST', body: { text }, idempotencyKey: newIdempotencyKey() });
  },
  deleteRoomLine(roomId: string, lineId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/lines/${enc(lineId)}`, { method: 'DELETE', idempotencyKey: newIdempotencyKey() });
  },
  /** An emoji on a line, or this person's taken off it (`emoji` null) — one per person per line (需求四). The answer is the line's emoji as this person sees them. */
  reactInRoom(roomId: string, lineId: string, emoji: string | null): Promise<{ ok: true; reactions: RoomReaction[] }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/lines/${enc(lineId)}/reaction`, { method: 'PUT', body: { emoji }, idempotencyKey: newIdempotencyKey() });
  },
  reportRoomLine(roomId: string, lineId: string): Promise<{ ok: true }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/lines/${enc(lineId)}/report`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
  },
  /** The room composer's mic (2026-09-30): the clip in, the words out; nothing stored. */
  transcribeRoomAudio(roomId: string, audio: Blob, signal?: AbortSignal): Promise<{ text: string }> {
    return apiFetch<{ text: string }>(`/api/rooms/${enc(roomId)}/transcribe`, { method: 'POST', body: audio, headers: { 'Content-Type': audio.type }, signal });
  },
  /** `opened`: the room put on screen (a coming back, which may bring a note from Ember; 2026-10-09), not a line read as it landed. */
  roomSeen(roomId: string, opened = false): Promise<{ ok: true }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/seen`, { method: 'POST', body: { ...(opened ? { opened: true } : {}), ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  leaveRoom(roomId: string, takeLines: boolean): Promise<{ ok: true }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/leave`, { method: 'POST', body: { take_lines: takeLines }, idempotencyKey: newIdempotencyKey() });
  },
  roomClosingFelt(roomId: string, felt: RoomFelt[]): Promise<{ ok: true }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/closing-felt`, { method: 'POST', body: { felt }, idempotencyKey: newIdempotencyKey() });
  },
  stopLookingForRoom(intentId: string): Promise<{ withdrawn: number }> {
    return apiFetch(`/api/me/room-intents/${enc(intentId)}/withdraw`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
  },

  // ---- New friends (2026-10-26 founder; edge friends.js) ----

  /** The Meet people page: `{ eligible: false }` alone for someone who may not take part. */
  friends(): Promise<FriendsPage> {
    return apiFetch('/api/me/friends');
  },
  /** The home opened the Meet people intro by itself: not again, on any device. */
  friendsIntroShown(): Promise<{ ok: true }> {
    return apiFetch('/api/me/friends/intro-shown', { method: 'POST', body: {} });
  },
  /** What Ember drafts for a card from the person's conversation (a line, topics). */
  friendDraft(): Promise<{ line: string; topics: string[] }> {
    return apiFetch('/api/me/friends/draft');
  },
  /** Their seat in a room they would put up on `topic`: the line Ember drafts for it, the name and picture they would have there, the room's id-to-be. */
  topicSeatDraft(topic: string): Promise<{ line: string; seat: TopicSeat }> {
    return apiFetch(`/api/me/friends/draft?topic=${enc(topic)}`);
  },
  /** File a request to meet a new friend, or change the card of the one that is looking. */
  fileFriendRequest(card: Partial<FriendCard>): Promise<{ request: FriendRequest }> {
    return apiFetch('/api/me/friends/request', { method: 'POST', body: { card, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  stopFriendRequest(): Promise<{ ok: true }> {
    return apiFetch('/api/me/friends/request/stop', { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
  },
  friendIntro(roomId: string): Promise<FriendIntroPage> {
    return apiFetch(`/api/friends/intro/${enc(roomId)}`);
  },
  /** Yes (with the opener picked, or none) or not now. */
  answerFriendIntro(roomId: string, accept: boolean, opener = ''): Promise<{ ok: true; answer: 'yes' | 'no'; room_status?: RoomStatus; opened?: boolean }> {
    return apiFetch(`/api/friends/intro/${enc(roomId)}/answer`, { method: 'POST', body: { accept, opener, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  /** Ask Ember on the introduction page: answered from the other's card and the introduction alone. */
  askAboutFriend(roomId: string, question: string): Promise<{ answer: string }> {
    return apiFetch(`/api/friends/intro/${enc(roomId)}/ask`, { method: 'POST', body: { question }, idempotencyKey: newIdempotencyKey() });
  },
  friendCheckin(roomId: string): Promise<FriendCheckinPage> {
    return apiFetch(`/api/friends/checkin/${enc(roomId)}`);
  },
  answerFriendCheckin(roomId: string, answer: 'keep' | 'okay' | 'stop'): Promise<{ ok: true }> {
    return apiFetch(`/api/friends/checkin/${enc(roomId)}`, { method: 'POST', body: { answer }, idempotencyKey: newIdempotencyKey() });
  },
  /** Put a topic up: rooms near it come back first (`near`) unless `anyway`; else the room of one. */
  putUpTopic(body: { room_id: string; topic: string; intro?: string; intro_edited?: boolean; anyway?: boolean }): Promise<{ near: TopicRoomRow[] } | { room_id: string }> {
    return apiFetch('/api/me/rooms/topics', { method: 'POST', body: { ...body, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  /** A topic room someone else put up, as one deciding whether to come in sees it, with the line Ember drafts for them. */
  topicRoom(roomId: string): Promise<TopicRoomPreview> {
    return apiFetch(`/api/friends/rooms/${enc(roomId)}`);
  },
  /** Ask into it: the fit read on the spot; their own line kept when they changed the drafted one. */
  applyToTopicRoom(roomId: string, intro = '', introEdited = false): Promise<{ fit: true; room_status: RoomStatus; line: string } | { fit: false }> {
    return apiFetch(`/api/friends/rooms/${enc(roomId)}/apply`, { method: 'POST', body: { intro, intro_edited: introEdited, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },
  /** Take Ember's invitation into it (the card a reply brought): straight in. */
  joinTopicRoom(roomId: string, intro = '', introEdited = false): Promise<{ ok: true; room_status: RoomStatus; present?: number; opens_on?: number }> {
    return apiFetch(`/api/friends/rooms/${enc(roomId)}/join`, { method: 'POST', body: { intro, intro_edited: introEdited, ...timezoneField() }, idempotencyKey: newIdempotencyKey() });
  },

  // ---- Groups (2026-11-03 founder; edge peer-groups.js) ----

  /** The groups open to them, and the others by name; `{ open: false }` for someone with none. 503 groups_unavailable: off. */
  groups(): Promise<GroupsList> {
    return apiFetch('/api/groups');
  },
  /** A group's page: the group alone until they join, then every post. 404 not_open. */
  group(groupId: string): Promise<GroupPage> {
    return apiFetch(`/api/groups/${enc(groupId)}`);
  },
  joinGroup(groupId: string): Promise<{ joined: true }> {
    return apiFetch(`/api/groups/${enc(groupId)}/join`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
  },
  leaveGroup(groupId: string): Promise<{ left: boolean }> {
    return apiFetch(`/api/groups/${enc(groupId)}/leave`, { method: 'POST', body: {}, idempotencyKey: newIdempotencyKey() });
  },
  /** A post and every answer under it; opening it counts as a view. 404 not_open (with group_id) | post_not_found. */
  groupPost(postId: string): Promise<GroupPostPage> {
    return apiFetch(`/api/groups/posts/${enc(postId)}`);
  },
  /** The post Ember wrote up from their conversation (its strings may be empty). */
  groupDraft(groupId: string): Promise<GroupDraft> {
    return apiFetch(`/api/groups/${enc(groupId)}/draft`);
  },
  /** Ember's look before a post or an answer goes up. 422 text_rejected | risk_to_self, 429 too_many, 503 check_unavailable. */
  checkGroupWords(groupId: string, body: { kind: GroupWritingKind; question?: string; body?: string; text?: string }): Promise<GroupCheck> {
    return apiFetch(`/api/groups/${enc(groupId)}/check`, { method: 'POST', body });
  },
  postToGroup(groupId: string, body: { kind: GroupMemberPostKind; question: string; body: string; via: 'page' | 'draft' }): Promise<{ post_id: string; group_id: string }> {
    return apiFetch(`/api/groups/${enc(groupId)}/posts`, { method: 'POST', body, idempotencyKey: newIdempotencyKey() });
  },
  /**
   * An answer to the post, or with `parentId` a reply under one of its top-level answers. 404 answer_not_found. A chain's
   * line is an answer without `parentId`: one per person (409 already_added), at most 280 characters (text_too_long).
   */
  answerGroupPost(postId: string, text: string, parentId: string | null = null): Promise<{ answer_id: string }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}/answers`, { method: 'POST', body: parentId ? { text, parent_id: parentId } : { text }, idempotencyKey: newIdempotencyKey() });
  },
  /** Their emoji on the post (or, with `answerId`, an answer or reply); null takes theirs off. → that item's emoji. 400 unknown_emoji. */
  reactInGroup(postId: string, emoji: string | null, answerId: string | null = null): Promise<{ reactions: GroupReaction[] }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}/reaction`, { method: 'PUT', body: answerId ? { emoji, answer_id: answerId } : { emoji }, idempotencyKey: newIdempotencyKey() });
  },
  /** A post (or, with `answerId`, one answer) reported: hidden for them from then on. */
  reportGroupPost(postId: string, answerId: string | null = null): Promise<{ reported: true }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}/report`, { method: 'POST', body: answerId ? { answer_id: answerId } : {}, idempotencyKey: newIdempotencyKey() });
  },
  /** Their own post taken down, for everyone, with its answers. not_yours | post_not_found. */
  deleteGroupPost(postId: string): Promise<{ removed: true }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}`, { method: 'DELETE', idempotencyKey: newIdempotencyKey() });
  },
  /** Their own answer (with the replies under it), reply or chain line taken down. not_yours | answer_not_found | post_not_found. */
  deleteGroupAnswer(postId: string, answerId: string): Promise<{ removed: true }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}/answers/${enc(answerId)}`, { method: 'DELETE', idempotencyKey: newIdempotencyKey() });
  },
  /** Their vote in a poll (the option's index), another replacing it → the poll as they now see it. not_a_poll | bad_choice | not_open | post_not_found. */
  voteInGroup(postId: string, choice: number): Promise<{ poll: GroupPoll }> {
    return apiFetch(`/api/groups/posts/${enc(postId)}/vote`, { method: 'PUT', body: { choice }, idempotencyKey: newIdempotencyKey() });
  },

  // ---- The Chats list (2026-10-20) ----

  /** The live channel's door (2026-10-21, live.ts): the websocket url and this person's connection token; 503 when the channel is off. */
  liveToken(): Promise<{ url: string; token: string; expires_at: number }> {
    return apiFetch('/api/me/live-token', { method: 'POST' });
  },
  /** The conversation's channel (phase 3): its subscription token; 404 when the conversation is not this person's, 503 when the channel is off. */
  conversationLiveToken(conversationId: string): Promise<{ channel: string; token: string; expires_at: number }> {
    return apiFetch(`/api/conversations/${enc(conversationId)}/live-token`, { method: 'POST' });
  },
  /** The Result page's draft so far (the hub's snapshot), read once on joining the channel; `snapshot` null when nothing is being written. */
  resultProgressSnapshot(conversationId: string): Promise<{ snapshot: Record<string, unknown> | null }> {
    return apiFetch(`/api/results/${enc(conversationId)}/progress-snapshot`);
  },
  chats(): Promise<ChatsResponse> {
    return apiFetch('/api/me/chats');
  },
  /** A room to the top of the person's list, or off it (404 not_pinnable for a room they no longer sit in). */
  pinRoom(roomId: string, pinned: boolean): Promise<{ pinned: boolean }> {
    return apiFetch(`/api/rooms/${enc(roomId)}/pin`, { method: 'POST', body: { pinned }, idempotencyKey: newIdempotencyKey() });
  },
  /** The person has their conversation with Ember on screen through this turn: Ember's later turns are what is unread. */
  conversationSeen(conversationId: string, turnIndex: number): Promise<{ ok: true }> {
    return apiFetch(`/api/conversations/${enc(conversationId)}/seen`, { method: 'POST', body: { turn_index: turnIndex }, idempotencyKey: newIdempotencyKey() });
  },

  // ---- The notebook's lock (§4.12, 2026-09-07) ----

  /** Is the notebook locked, and can a way back in be texted. */
  notebookLock(): Promise<NotebookLockResponse> {
    // The dev shell runs without a session (like meSummary below), and a
    // notebook nobody can unlock would put the whole page behind a cover.
    if (import.meta.env.DEV) {
      return Promise.resolve({
        ok: true, locked: false, set_at: null,
        recovery: { available: false, hint: '', reason: 'no_handle' },
        reauth: { methods: [], phone_hint: '' },
      });
    }
    return apiFetch('/api/me/notebook-lock');
  },

  /** The cover's password. Throws `wrong_password` (403) when it is not it. */
  openNotebook(password: string): Promise<{ ok: true; opened: boolean }> {
    return apiFetch('/api/me/notebook-lock/open', {
      method: 'POST', body: { password }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /**
   * Put a password on, or change it. A notebook that already has one needs
   * either the current password or a `ticket` from the texted link — the two
   * proofs, and the reason the lock can no longer be lifted by whoever is
   * holding the phone.
   */
  setNotebookLock(body: {
    password: string;
    current_password?: string;
    ticket?: string;
    source?: 'close' | 'change' | 'reset' | 'migrated';
  }): Promise<{ ok: true; locked: true }> {
    return apiFetch('/api/me/notebook-lock/set', {
      method: 'POST', body, idempotencyKey: newIdempotencyKey(),
    });
  },

  removeNotebookLock(body: { current_password?: string; ticket?: string }): Promise<{ ok: true; locked: false }> {
    return apiFetch('/api/me/notebook-lock/remove', {
      method: 'POST', body, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** Confirm it's you by a code texted to the account's own number (the edge picks it; 2026-10-19). */
  reauthPhoneStart(): Promise<{ ok: true; challenge_id: string }> {
    return apiFetch('/api/me/reauth/phone/start', {
      method: 'POST', body: {}, idempotencyKey: newIdempotencyKey(),
    });
  },

  reauthPhoneVerify(challengeId: string, code: string): Promise<{ ok: true; method: string }> {
    return apiFetch('/api/me/reauth/phone/verify', {
      method: 'POST', body: { challenge_id: challengeId, code }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** A new password within five minutes of Confirm it's you; `reauth_required` (403) once that has run out. */
  resetNotebookLock(password: string): Promise<{ ok: true; locked: true }> {
    return apiFetch('/api/me/notebook-lock/reset', {
      method: 'POST', body: { password }, idempotencyKey: newIdempotencyKey(),
    });
  },

  /** `Forgot it?` — text this account's own phone the way back in. */
  recoverNotebookLock(): Promise<{ ok: true; sent: true; hint: string }> {
    return apiFetch('/api/me/notebook-lock/recover', {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },

  meSummary(): Promise<MeSummaryResponse> {
    if (import.meta.env.DEV) return Promise.resolve(DEV_ME_SUMMARY);
    return apiFetch('/api/me/summary');
  },

  /** The notebook's Letters tab (§4.20): every weekly letter Ember wrote this person, newest first. */
  meLetters(): Promise<{ letters: WeeklyLetter[] }> {
    return apiFetch('/api/me/letters');
  },
  /** One weekly letter, the person's own; opening it is reading it (read_at). */
  meLetter(letterId: string): Promise<{ letter: WeeklyLetter }> {
    return apiFetch(`/api/me/letters/${encodeURIComponent(letterId)}`);
  },
  /** §4.20 mint (or fetch) this letter's hand-off link. The person's own letter; idempotent; never taken back. */
  mintLetterHandoff(letterId: string): Promise<{ handoff_path: string }> {
    return apiFetch(`/api/me/letters/${encodeURIComponent(letterId)}/handoff`, {
      method: 'POST', idempotencyKey: newIdempotencyKey(),
    });
  },
  /** §4.20 the recipient's read of a letter's hand-off link. 404 = no such link. */
  letterHandoff(token: string): Promise<LetterHandoffResponse> {
    return apiFetch<LetterHandoffResponse>(`/api/letter-handoff/${enc(token)}`);
  },
};
